import {startTransition, Suspense, useCallback, useMemo} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'

import {useMergeMethodContext} from '../contexts/MergeMethodContext'
import {useSuspenseMergeBoxPageData} from '../page-data/loaders/use-merge-box-page-data'
import {useStatusChecksPageData} from '../page-data/loaders/use-status-checks-page-data'
import {useMergeabilityLiveUpdates, type Channels} from '../hooks/use-mergeability-live-updates'
import {useStatusChecksLiveUpdates} from '../hooks/use-status-checks-live-updates'
import {MergeBoxStatusCalculator} from '../helpers/merge-box-status-calculator/merge-box-status-calculator'
import {
  reviewStatusPresentation,
  checksStatusPresentation,
  conflictsStatusPresentation,
  draftStatusPresentation,
  type CondensedStatusRow,
} from '../helpers/condensed-merge-status-presentation'
import type {JSONAPIPullRequestPayload, PullRequestMergeRequirementsPayload} from '../page-data/payloads/merge-box'
import type {StatusChecksPageData} from '../page-data/payloads/status-checks'
import styles from './CondensedMergeStatus.module.css'
import {CondensedMergeStatusErrorFallback} from './CondensedMergeStatusErrorFallback'
import {CondensedMergeStatusSkeleton} from './CondensedMergeStatusSkeleton'

interface CondensedMergeStatusProps {
  headSha: string
  channels: Channels | null
}

export function CondensedMergeStatusWithSuspense(props: CondensedMergeStatusProps) {
  return (
    <ErrorBoundary boundaryName="CondensedMergeStatus" fallback={<CondensedMergeStatusErrorFallback />}>
      <Suspense fallback={<CondensedMergeStatusSkeleton />}>
        <CondensedMergeStatus {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}

/**
 * A condensed merge status component that displays the overall mergeability status of a pull request.
 *
 * Designed to be used in the sidebar of the pull request overview page.
 *
 * This is prototype and wider adoption is not recommended at this time.
 * @experimental
 */
export function CondensedMergeStatus({headSha, channels}: CondensedMergeStatusProps) {
  const {mergeMethod} = useMergeMethodContext()
  const {data, refetch} = useSuspenseMergeBoxPageData({mergeMethod})
  const {
    data: statusChecksData,
    data: {aliveChannels},
  } = useStatusChecksPageData({pullRequestHeadSha: headSha})

  const refetchQuery = useCallback(() => {
    startTransition(() => {
      refetch({cancelRefetch: false})
    })
  }, [refetch])

  useMergeabilityLiveUpdates({refetchQuery, channels: channels ?? EMPTY_CHANNELS})
  useStatusChecksLiveUpdates(aliveChannels.commitHeadShaChannel, headSha)

  const {pullRequest, mergeRequirements} = data

  // Don't render for closed or merged PRs
  if (pullRequest.state === 'CLOSED' || pullRequest.state === 'MERGED') {
    return null
  }

  return (
    <CondensedMergeStatusContent
      pullRequest={pullRequest}
      mergeRequirements={mergeRequirements}
      statusChecksData={statusChecksData}
    />
  )
}

const EMPTY_CHANNELS: Channels = {
  stateChannel: null,
  deployedChannel: null,
  reviewStateChannel: null,
  workflowsChannel: null,
  mergeQueueChannel: null,
  headRefChannel: null,
  baseRefChannel: null,
  gitMergeStateChannel: null,
  pullRequestChannel: null,
}

function CondensedMergeStatusContent({
  pullRequest,
  mergeRequirements,
  statusChecksData,
}: {
  pullRequest: JSONAPIPullRequestPayload
  mergeRequirements: PullRequestMergeRequirementsPayload | null
  statusChecksData: StatusChecksPageData
}) {
  const calculator = useMemo(
    () => new MergeBoxStatusCalculator(pullRequest, mergeRequirements, statusChecksData),
    [pullRequest, mergeRequirements, statusChecksData],
  )

  const {
    DraftStateSection: draftSection,
    ReviewerSection: reviewerSection,
    ChecksSection: checksSection,
    ConflictsSection: conflictsSection,
  } = calculator.sections

  const rows: CondensedStatusRow[] = []

  if (draftSection.shouldRender) {
    rows.push(draftStatusPresentation(draftSection))
  }
  if (reviewerSection.shouldRender) {
    rows.push(reviewStatusPresentation(reviewerSection))
  }
  if (checksSection.shouldRender) {
    rows.push(checksStatusPresentation(checksSection, statusChecksData))
  }
  if (conflictsSection.shouldRender) {
    rows.push(conflictsStatusPresentation(conflictsSection))
  }

  if (rows.length === 0) return null

  return (
    <ul className={styles.statusList}>
      {rows.map(row => (
        <li key={row.label} className={styles.statusRow}>
          <span style={{color: row.iconColor, display: 'flex'}}>
            <row.icon size={16} />
          </span>
          <span className={styles.statusText}>
            {row.label}
            {row.detail && (
              <>
                {' '}
                <span className={styles.statusDetail}>{row.detail}</span>
              </>
            )}
          </span>
        </li>
      ))}
    </ul>
  )
}
